import type { ExperimentalMutations } from '../__types__/ExperimentalMutations';
import { fetchJSON } from '../fetchJSON';
import { MediaType } from '../MediaType';

const prepareFormData = (eventData: any) => {
	const formData = new FormData();

	Object.entries(eventData).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((obj) => {
				Object.entries(obj).forEach(([k, v]) => {
					formData.append(k, v as any);
				});
			});
		} else if (value) {
			formData.append(key, value as any);
		}
	});

	return formData;
};

export const experimentalTeamCalendarsAddEvent: ExperimentalMutations['experimentalTeamCalendarsAddEvent'] =
	(_, eventData, { network }) => {
		const formData = prepareFormData(eventData);

		return fetchTeamCalendarJSON(network, `/wiki/rest/calendar-services/1.0/calendar/events.json`, {
			method: 'PUT',
			// FIXME this isn't exactly correct type-wise. The easiest fix here is to
			// append to `URLSearchParams` themselves, as opposed to going through `FormData` first
			// @ts-ignore
			body: new URLSearchParams(formData).toString(),
		}).then(
			(data) => data || {},
			(reason) => {
				if (network.BadStatusError.isBadStatusError(reason)) {
					// FIXME The type Reponse is not compatible with the type
					// ExperimentalTeamCalendarsAddEventResponse so ApolloClient will
					// throw at runtime!
					return reason.response;
				}

				throw reason;
			},
		);
	};

export const experimentalTeamCalendarEdit: ExperimentalMutations['experimentalTeamCalendarEdit'] = (
	_,
	eventData,
	{ network },
) => {
	const formData = prepareFormData(eventData);

	return fetchTeamCalendarJSON(
		network,
		`/wiki/rest/calendar-services/1.0/calendar/subcalendars.json`,
		{
			method: 'PUT',
			// FIXME this isn't exactly correct type-wise. The easiest fix here is to
			// append to `URLSearchParams` themselves, as opposed to going through `FormData` first
			// @ts-ignore
			body: new URLSearchParams(formData).toString(),
		},
	);
};

export const experimentalTeamCalendarDelete: ExperimentalMutations['experimentalTeamCalendarDelete'] =
	(_, { subCalendarId, calendarContext, viewingSpaceKey }, { network }) => {
		const searchParams = new URLSearchParams({
			subCalendarId,
			calendarContext,
		});
		if (viewingSpaceKey) {
			searchParams.append('viewingSpaceKey', viewingSpaceKey);
		}

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/subcalendars.json`,
			{
				method: 'DELETE',
				body: searchParams.toString(),
			},
		);
	};

export const experimentalTeamCalendarResetSubscribeLink: ExperimentalMutations['experimentalTeamCalendarResetSubscribeLink'] =
	(_, { subCalendarId }, { network }) => {
		return network
			.cfetch(
				`/wiki/rest/calendar-services/1.0/calendar/subcalendar/privateurl/reset/${subCalendarId}`,
				{
					method: 'PUT',
					headers: {
						'X-Requested-With': 'XMLHttpRequest',
					},
				},
			)
			.then(() => true);
	};

export const experimentalTeamCalendarSubscribe: ExperimentalMutations['experimentalTeamCalendarSubscribe'] =
	(_, eventData, { network }) => {
		const formData = new URLSearchParams({
			color: eventData.color,
			watchSubCalendars: String(eventData.watchSubCalendars),
			calendarContext: String(eventData.calendarContext),
			viewingSpaceKey: String(eventData.viewingSpaceKey),
		});
		eventData.subCalendarIds.forEach((subCalendarId) => {
			formData.append('subCalendarIds', subCalendarId);
		});

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/subcalendars/subscribe.json`,
			{
				method: 'PUT',
				body: formData.toString(),
			},
		);
	};

export const experimentalTeamCalendarDeleteEvent: ExperimentalMutations['experimentalTeamCalendarDeleteEvent'] =
	(_, { subCalendarId, uid }, { network }) => {
		const searchParams = new URLSearchParams();

		searchParams.append('subCalendarId', subCalendarId);
		searchParams.append('uid', uid);

		return fetchTeamCalendarJSON(network, `/wiki/rest/calendar-services/1.0/calendar/events.json`, {
			method: 'DELETE',
			body: searchParams.toString(),
		});
	};

export const experimentalTeamCalendarImport: ExperimentalMutations['experimentalTeamCalendarImport'] =
	(
		_,
		{
			calendarId,
			decorator,
			color,
			spaceKey,
			name,
			description,
			spaceKeyAutocomplete,
			file_0,
			timeZoneId,
		},
		{ network },
	) => {
		const formData = new FormData();
		formData.append('calendarId', String(calendarId));
		formData.append('decorator', String(decorator));
		formData.append('color', String(color));
		formData.append('spaceKey', String(spaceKey));
		formData.append('name', String(name));
		formData.append('description', String(description));
		formData.append('spaceKeyAutocomplete', String(spaceKeyAutocomplete));
		formData.append('file_0', file_0);
		if (timeZoneId) {
			formData.append('timeZoneId', String(timeZoneId));
		}

		return network
			.cfetch(`/wiki/plugins/calendar/importsubcalendar.action`, {
				method: 'POST',
				headers: {
					'X-Atlassian-Token': 'no-check',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: formData,
			})
			.then((res) => res.text())
			.then((res) => {
				const responseDocument = document.createElement('html');
				responseDocument.innerHTML = res;

				if (responseDocument.querySelector('.subcalendar-id')) {
					return {
						success: true,
						subCalendarId: responseDocument.querySelector('.subcalendar-id')?.innerHTML,
					};
				}

				if (responseDocument.querySelector('.import-timezone-required')) {
					//We could not determine the calendars timezone
					return {
						success: false,
						fieldErrors: [
							{
								field: 'timeZoneId',
								errorMessages: [],
							},
						],
					};
				}

				if (!responseDocument.querySelector('.import-successful')) {
					const response: {
						success: boolean;
						fieldErrors: {
							field: string;
							errorMessages: [string];
						}[];
					} = {
						success: false,
						fieldErrors: [],
					};

					responseDocument.querySelectorAll('.field-errors input').forEach((el) => {
						response.fieldErrors.push({
							field: (el as HTMLInputElement).name,
							errorMessages: [(el as HTMLInputElement).value],
						});
					});

					return response;
				}

				throw 'Unexpected response';
			});
	};

export const experimentalTeamCalendarUpdateViewPreference: ExperimentalMutations['experimentalTeamCalendarUpdateViewPreference'] =
	(_, { view }, { network }) => {
		const searchParams = new URLSearchParams();
		searchParams.append('view', view);
		return network
			.cfetch(`/wiki/rest/calendar-services/1.0/calendar/preferences/view.json`, {
				method: 'PUT',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': `${MediaType.X_WWW_FORM_URLENCODED}; charset=UTF-8`,
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: searchParams.toString(),
			})
			.then(() => true);
	};

export const experimentalTeamCalendarEventTypeDefaultUpdate: ExperimentalMutations['experimentalTeamCalendarEventTypeDefaultUpdate'] =
	(_, { title, icon, subCalendarId, eventTypeId, periodInMins, created }, { network }) => {
		const searchParams = new URLSearchParams();
		searchParams.append('title', title);
		searchParams.append('icon', icon);
		searchParams.append('subCalendarId', subCalendarId);
		searchParams.append('eventTypeId', eventTypeId);
		searchParams.append('periodInMins', String(periodInMins));
		searchParams.append('created', String(created));

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/eventtype/sandbox.json`,
			{
				method: 'PUT',
				body: searchParams.toString(),
			},
		);
	};

export const experimentalTeamCalendarJiraJQLValidate: ExperimentalMutations['experimentalTeamCalendarJiraJQLValidate'] =
	(_, { serverId, jql }, { network }) => {
		const searchParams = new URLSearchParams();
		searchParams.append('jql', jql);

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/jira/${serverId}/jql/validate.json`,
			{
				method: 'PUT',
				body: searchParams.toString(),
			},
		);
	};

export const experimentalTeamCalendarEventTypeCustomUpdate: ExperimentalMutations['experimentalTeamCalendarEventTypeCustomUpdate'] =
	(_, { title, icon, subCalendarId, customEventTypeId, periodInMins, created }, { network }) => {
		const searchParams = new URLSearchParams();
		searchParams.append('title', title);
		searchParams.append('icon', icon);
		searchParams.append('subCalendarId', subCalendarId);
		searchParams.append('customEventTypeId', customEventTypeId);
		searchParams.append('periodInMins', String(periodInMins));
		searchParams.append('created', String(created));

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/eventtype/custom.json`,
			{
				method: 'PUT',
				body: searchParams.toString(),
			},
		);
	};

export const experimentalTeamCalendarEventTypeCustomDelete: ExperimentalMutations['experimentalTeamCalendarEventTypeCustomDelete'] =
	(_, { subCalendarId, customEventTypeId }, { network }) => {
		const searchParams = new URLSearchParams();
		searchParams.append('subCalendarId', subCalendarId);
		searchParams.append('customEventTypeId', customEventTypeId);

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/eventtype/custom.json`,
			{
				method: 'DELETE',
				body: searchParams.toString(),
			},
		);
	};

export const experimentalTeamCalendarRestriction: ExperimentalMutations['experimentalTeamCalendarRestriction'] =
	(
		_,
		{
			subCalendarId,
			calendarContext,
			viewingSpaceKey,
			updateUsersPermittedToView,
			updateGroupsPermittedToView,
			updateUsersPermittedToEdit,
			updateGroupsPermittedToEdit,
			usersPermittedToView,
			usersPermittedToEdit,
			groupsPermittedToView,
			groupsPermittedToEdit,
		},
		{ network },
	) => {
		const searchParams = new URLSearchParams();
		searchParams.append('subCalendarId', String(subCalendarId));
		searchParams.append('calendarContext', calendarContext);
		searchParams.append('viewingSpaceKey', viewingSpaceKey);
		searchParams.append('updateUsersPermittedToView', String(updateUsersPermittedToView));
		searchParams.append('updateGroupsPermittedToView', String(updateGroupsPermittedToView));
		searchParams.append('updateUsersPermittedToEdit', String(updateUsersPermittedToEdit));
		searchParams.append('updateGroupsPermittedToEdit', String(updateGroupsPermittedToEdit));
		usersPermittedToView?.forEach((item: string) => {
			searchParams.append('usersPermittedToView', item);
		});
		usersPermittedToEdit?.forEach((item: string) => {
			searchParams.append('usersPermittedToEdit', item);
		});
		groupsPermittedToView?.forEach((item: string) => {
			searchParams.append('groupsPermittedToView', item);
		});
		groupsPermittedToEdit?.forEach((item: string) => {
			searchParams.append('groupsPermittedToEdit', item);
		});

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/subcalendars/restrictions.json`,
			{
				method: 'PUT',
				body: searchParams.toString(),
			},
		);
	};

export const experimentalTeamCalendarColors: ExperimentalMutations['experimentalTeamCalendarColors'] =
	(_, { subCalendarId, color, calendarContext, viewingSpaceKey }, { network }) => {
		const formData = new URLSearchParams({
			subCalendarId,
			color,
			calendarContext,
			viewingSpaceKey,
		});

		return fetchTeamCalendarJSON(
			network,
			`/wiki/rest/calendar-services/1.0/calendar/subcalendars/color.json`,
			{
				method: 'PUT',
				body: formData.toString(),
			},
		);
	};

export const experimentalTeamCalendarDeleteSingleEvent: ExperimentalMutations['experimentalTeamCalendarDeleteSingleEvent'] =
	(_, { subCalendarId, uid, originalStart, singleInstance, recurrenceId }, { network }) => {
		const searchParams = new URLSearchParams();

		searchParams.append('subCalendarId', subCalendarId);
		searchParams.append('uid', uid);

		if (originalStart) {
			searchParams.append('originalStart', originalStart);
		}
		if (singleInstance != null) {
			searchParams.append('singleInstance', String(singleInstance));
		}
		if (recurrenceId) {
			searchParams.append('recurrenceId', recurrenceId);
		}

		return fetchTeamCalendarJSON(network, `/wiki/rest/calendar-services/1.0/calendar/events.json`, {
			method: 'DELETE',
			body: searchParams.toString(),
		});
	};

export const experimentalTeamCalendarDeleteAllFutureEvents: ExperimentalMutations['experimentalTeamCalendarDeleteAllFutureEvents'] =
	(_, { subCalendarId, uid, recurUntil }, { network }) => {
		const searchParams = new URLSearchParams();

		searchParams.append('subCalendarId', subCalendarId);
		searchParams.append('uid', uid);
		if (recurUntil) {
			searchParams.append('recurUntil', recurUntil);
		}

		return fetchTeamCalendarJSON(network, `/wiki/rest/calendar-services/1.0/calendar/events.json`, {
			method: 'DELETE',
			body: searchParams.toString(),
		});
	};

const fetchTeamCalendarJSON: typeof fetchJSON = (network, resource, init) =>
	fetchJSON(network, resource, {
		...init,
		headers: {
			'Content-Type': `${MediaType.X_WWW_FORM_URLENCODED}; charset=UTF-8`,
			'X-Requested-With': 'XMLHttpRequest',
			...init?.headers,
		},
	});
